import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { gamificationAPI } from '../lib/api';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { 
  Trophy, 
  Medal,
  Crown,
  Star,
  Award,
  TrendingUp,
  Users,
  GitBranch,
  Flame,
  Zap,
  HelpCircle,
  MessageCircle,
  ThumbsUp,
  Mountain,
} from 'lucide-react';
import { toast } from 'sonner';

// Icon mapping for badges
const BadgeIcons = {
  'help-circle': HelpCircle,
  'git-branch': GitBranch,
  'thumbs-up': ThumbsUp,
  'message-circle': MessageCircle,
  'tree': GitBranch,
  'trees': GitBranch,
  'mountain': Mountain,
  'star': Star,
  'award': Award,
  'crown': Crown,
  'flame': Flame,
  'zap': Zap,
};

const getRankIcon = (rank) => {
  switch(rank) {
    case 1: return <Crown className="w-6 h-6 text-yellow-500" />;
    case 2: return <Medal className="w-6 h-6 text-slate-400" />;
    case 3: return <Medal className="w-6 h-6 text-amber-600" />;
    default: return <span className="text-lg font-bold text-slate-400">#{rank}</span>;
  }
};

const getRankBg = (rank) => {
  switch(rank) {
    case 1: return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
    case 2: return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200';
    case 3: return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
    default: return 'bg-white border-slate-200';
  }
};

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [badges, setBadges] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leaderboard'); // 'leaderboard' | 'badges'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leaderboardRes, badgesRes] = await Promise.all([
        gamificationAPI.getLeaderboard(50),
        gamificationAPI.getAllBadges(),
      ]);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setBadges(badgesRes.data.badges);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="spinner" data-testid="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="leaderboard-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Leaderboard</h1>
          <p className="text-slate-500">Top contributors building knowledge together</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-8">
          <Button
            variant={activeTab === 'leaderboard' ? 'default' : 'outline'}
            onClick={() => setActiveTab('leaderboard')}
            className={activeTab === 'leaderboard' ? 'bg-slate-900' : ''}
            data-testid="tab-leaderboard"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Top Contributors
          </Button>
          <Button
            variant={activeTab === 'badges' ? 'default' : 'outline'}
            onClick={() => setActiveTab('badges')}
            className={activeTab === 'badges' ? 'bg-slate-900' : ''}
            data-testid="tab-badges"
          >
            <Award className="w-4 h-4 mr-2" />
            All Badges
          </Button>
        </div>

        {activeTab === 'leaderboard' ? (
          <>
            {/* Top 3 Podium */}
            {leaderboard.length >= 3 && (
              <div className="flex justify-center items-end gap-4 mb-8">
                {/* 2nd Place */}
                <div className="flex flex-col items-center">
                  <Link to={`/profile/${leaderboard[1].user_id}`}>
                    <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center text-xl font-bold text-slate-600 mb-2 border-4 border-slate-300 hover:border-slate-400 transition-colors overflow-hidden">
                      {leaderboard[1].picture ? (
                        <img src={leaderboard[1].picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        leaderboard[1].name?.charAt(0).toUpperCase()
                      )}
                    </div>
                  </Link>
                  <Medal className="w-6 h-6 text-slate-400 mb-1" />
                  <p className="font-semibold text-slate-900 text-sm">{leaderboard[1].name}</p>
                  <p className="text-orange-500 font-bold">{leaderboard[1].points} pts</p>
                  <div className="w-20 h-24 bg-slate-200 rounded-t-lg mt-2"></div>
                </div>

                {/* 1st Place */}
                <div className="flex flex-col items-center">
                  <Link to={`/profile/${leaderboard[0].user_id}`}>
                    <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center text-2xl font-bold text-yellow-600 mb-2 border-4 border-yellow-400 hover:border-yellow-500 transition-colors overflow-hidden">
                      {leaderboard[0].picture ? (
                        <img src={leaderboard[0].picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        leaderboard[0].name?.charAt(0).toUpperCase()
                      )}
                    </div>
                  </Link>
                  <Crown className="w-8 h-8 text-yellow-500 mb-1" />
                  <p className="font-bold text-slate-900">{leaderboard[0].name}</p>
                  <p className="text-orange-500 font-bold text-lg">{leaderboard[0].points} pts</p>
                  <div className="w-24 h-32 bg-yellow-400 rounded-t-lg mt-2"></div>
                </div>

                {/* 3rd Place */}
                <div className="flex flex-col items-center">
                  <Link to={`/profile/${leaderboard[2].user_id}`}>
                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center text-xl font-bold text-amber-600 mb-2 border-4 border-amber-300 hover:border-amber-400 transition-colors overflow-hidden">
                      {leaderboard[2].picture ? (
                        <img src={leaderboard[2].picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        leaderboard[2].name?.charAt(0).toUpperCase()
                      )}
                    </div>
                  </Link>
                  <Medal className="w-6 h-6 text-amber-600 mb-1" />
                  <p className="font-semibold text-slate-900 text-sm">{leaderboard[2].name}</p>
                  <p className="text-orange-500 font-bold">{leaderboard[2].points} pts</p>
                  <div className="w-20 h-16 bg-amber-300 rounded-t-lg mt-2"></div>
                </div>
              </div>
            )}

            {/* Full Leaderboard */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-500" />
                  All Contributors
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {leaderboard.map((user) => (
                  <Link
                    key={user.user_id}
                    to={`/profile/${user.user_id}`}
                    className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors ${getRankBg(user.rank)}`}
                    data-testid={`leaderboard-user-${user.user_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 flex justify-center">
                        {getRankIcon(user.rank)}
                      </div>
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600 overflow-hidden">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          user.name?.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.badge_count} badges</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-orange-500">{user.points}</p>
                      <p className="text-xs text-slate-500">points</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* Badges Tab */
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-slate-500" />
                Earn Badges
              </h2>
              <p className="text-sm text-slate-500 mt-1">Complete achievements to unlock badges and earn points</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {Object.entries(badges).map(([badgeId, badge]) => {
                const IconComponent = BadgeIcons[badge.icon] || Award;
                return (
                  <div
                    key={badgeId}
                    className="border border-slate-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-md transition-all"
                    data-testid={`badge-${badgeId}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900">{badge.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">{badge.description}</p>
                        <p className="text-xs text-orange-500 font-medium mt-2">+{badge.points} points</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
